import { ModelBase } from '../../abstract/model-base.model';
/**
 * Statut de connexion Open Banking (agnostique du prestataire)
 */
export class OpenBankingStatus extends ModelBase {

    static override CLASS_NAME: string = 'OpenBankingStatus';

    private _actionRequired!: boolean;
    private _aggregatorBalance!: number;
    private _label!: string;
    private _lastSync!: string | null;
    private _message!: string | null;
    private _nextSync!: string | null;
    private _provider!: OpenBankingProvider | null;
    private _state!: OpenBankingStateType;
    private _syncing!: boolean;

    /**
     * Indique si une action de l'utilisateur est requise
     * true = l'utilisateur doit faire quelque chose (se reconnecter, valider MFA, etc.)
     * false = rien à faire, ça peut se résoudre seul ou tout va bien
     */
    public get actionRequired(): boolean {
        return this._actionRequired;
    }

    public set actionRequired(value: any) {
        this._setValue('_actionRequired', value, 'boolean');
    }

    /**
     * Solde récupéré depuis l'agrégateur bancaire
     */
    public get aggregatorBalance(): number {
        return this._aggregatorBalance;
    }

    public set aggregatorBalance(value: any) {
        this._setValue('_aggregatorBalance', value, 'float');
    }

    /**
     * Vérifie si le statut indique une erreur
     */
    public hasIssue(): boolean {
        const errorStates: OpenBankingStateType[] = [
            'technical_error',
            'website_unavailable',
            'rate_limited',
            'temporarily_disabled',
            'migrating',
            'not_supported',
            'credentials_invalid',
            'mfa_required',
            'action_required',
            'password_expired'
        ];
        return errorStates.includes(this.state);
    }

    /**
     * Vérifie si la connexion est active (connectée ou en cours de synchronisation)
     */
    public isConnected(): boolean {
        return this.state !== 'not_connected';
    }

    /**
     * Vérifie si l'erreur est liée au prestataire (aucune action utilisateur possible)
     */
    public isProviderError(): boolean {
        const providerErrors: OpenBankingStateType[] = [
            'technical_error',
            'website_unavailable',
            'rate_limited',
            'temporarily_disabled',
            'migrating',
            'not_supported'
        ];
        return providerErrors.includes(this.state);
    }

    /**
     * Vérifie si la connexion est active (connectée ou en cours de synchronisation)
     */
    public isSynced(): boolean {
        return this.state === 'connected' || this.state === 'syncing' || this.state === 'partial_success';
    }

    /**
     * Vérifie si l'erreur nécessite une action de l'utilisateur
     */
    public isUserActionError(): boolean {
        const userActionErrors: OpenBankingStateType[] = [
            'credentials_invalid',
            'mfa_required',
            'action_required',
            'password_expired',
            'validating'
        ];
        return userActionErrors.includes(this.state);
    }

    public get label(): string {
        return this._label;
    }

    public set label(value: any) {
        this._setValue('_label', value, 'string');
    }

    /**
     * Date et heure de la dernière synchronisation réussie
     * Format ISO 8601, null si jamais synchronisé
     */
    public get lastSync(): string | null {
        return this._lastSync;
    }

    public set lastSync(value: any) {
        this._setValue('_lastSync', value, 'string');
    }

    /**
     * Message en français à afficher à l'utilisateur
     * null si pas d'erreur ou de message particulier
     */
    public get message(): string | null {
        return this._message;
    }

    public set message(value: any) {
        this._setValue('_message', value, 'string');
    }

    /**
     * Date et heure de la prochaine synchronisation prévue
     * Format ISO 8601, null si pas de prochaine synchro planifiée
     * (Bridge n'a pas de next_sync, toujours null)
     */
    public get nextSync(): string | null {
        return this._nextSync;
    }

    public set nextSync(value: any) {
        this._setValue('_nextSync', value, 'string');
    }

    /**
     * Nom du prestataire Open Banking utilisé
     * "bridge" | null si le compte n'est rattaché à aucun prestataire
     */
    public get provider(): OpenBankingProvider | null {
        return this._provider;
    }

    public set provider(value: any) {
        this._setValue('_provider', value, 'string');
    }

    /**
     * Statut de la connexion
     */
    public get state(): OpenBankingStateType {
        return this._state;
    }

    public set state(value: any) {
        this._setValue('_state', value, 'string');
    }

    /**
     * Indique si une synchronisation est en cours
     */
    public get syncing(): boolean {
        return this._syncing;
    }

    public set syncing(value: any) {
        this._setValue('_syncing', value, 'boolean');
    }
}

/**
 * Types de statut Open Banking
 *
 * États OK:
 * - connected: Connexion établie et fonctionnelle
 * - syncing: Synchronisation en cours
 * - partial_success: Synchronisation partielle réussie
 *
 * Erreurs provider (aucune action utilisateur):
 * - technical_error: Erreur technique du prestataire
 * - website_unavailable: Site bancaire indisponible
 * - rate_limited: Limite de taux API atteinte
 * - temporarily_disabled: Service temporairement désactivé
 * - migrating: Migration en cours
 * - not_supported: Fonctionnalité non supportée
 *
 * Action utilisateur requise:
 * - credentials_invalid: Identifiants invalides
 * - mfa_required: Authentification multi-facteurs requise
 * - action_required: Action générique requise
 * - password_expired: Mot de passe expiré
 * - validating: En attente de validation utilisateur
 *
 * États spéciaux:
 * - not_connected: Aucune connexion Open Banking
 * - paused: Synchronisation mise en pause
 * - disabled: Synchronisation désactivée
 * - deleted: Connexion supprimée
 */
export type OpenBankingStateType =
    | "connected"
    | "syncing"
    | "partial_success"
    | "technical_error"
    | "website_unavailable"
    | "rate_limited"
    | "temporarily_disabled"
    | "migrating"
    | "not_supported"
    | "credentials_invalid"
    | "mfa_required"
    | "action_required"
    | "password_expired"
    | "validating"
    | "not_connected"
    | "paused"
    | "disabled"
    | "deleted";

/**
 * Types de prestataires Open Banking supportés
 *
 * ⚠️ Un seul prestataire depuis le décommissionnement de Powens (#1068) : le serveur ne
 * renvoie plus que "bridge", ou null pour un compte non connecté. À ne pas confondre avec
 * l'origine `powens` d'une transaction bancaire, qui est une donnée historique conservée
 * et n'a jamais désigné un prestataire disponible — cf. `getSourceLabel()` de
 * `BankAccountLinesTableComponent`.
 */
export type OpenBankingProvider = "bridge";
